import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Radio, Layers, Lock } from "lucide-react";
import { SIGNUP_URL } from "./links";

const features = [
  { icon: ShieldCheck, label: "Simulated vault", copy: "Scenario holdings sealed inside your private simulation." },
  { icon: Radio, label: "Live market data", copy: "Every tick, as it prints — no refresh, no delay." },
  { icon: Layers, label: "4 asset classes", copy: "Crypto, equities, metals and forex in one book." },
  { icon: Lock, label: "Aureon ID · Private", copy: "One identity guards your whole training terminal." },
];

const hex = (n) =>
  Array.from({ length: n }, () => "0123456789ABCDEF"[Math.floor(Math.random() * 16)]).join("");

const makeLine = () => {
  const kind = Math.random();
  if (kind < 0.25) return { text: `VLT :: ${hex(8)}-${hex(8)} :: SEALED`, gold: true };
  if (kind < 0.5) return { text: `FEED >> ${hex(16)} :: ${(Math.random() * 9).toFixed(3)}ms`, gold: false };
  if (kind < 0.75) return { text: `KEY ${hex(6)} ROTATED :: AES-256 :: OK`, gold: false };
  return { text: `ID-AUR-${hex(10)} :: ENCRYPTED`, gold: true };
};

export default function EncryptedFeed() {
  const [lines, setLines] = useState(() => Array.from({ length: 16 }, makeLine));

  useEffect(() => {
    const t = setInterval(() => setLines((ls) => [...ls.slice(1), makeLine()]), 380);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="encrypted-feed-section" className="border-t border-white/10 bg-surface">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-2 lg:px-10 lg:py-36">
        <div className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">Security</p>
            <h2 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
              Guarded by the
              <br />
              <span className="italic text-gold">encrypted feed.</span>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/50">
              Behind the quiet interface sits heavy infrastructure — rotating
              keys and a live market data feed engineered for the few, not the
              crowd. Every book is a simulation.
            </p>
          </motion.div>

          <div className="mt-12">
            {features.map((f, i) => (
              <motion.div
                key={f.label}
                data-testid={`feed-feature-${i}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-start gap-5 border-t border-white/10 py-5 last:border-b"
              >
                <f.icon className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white">{f.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/45">{f.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.a
            data-testid="feed-signup-btn"
            href={SIGNUP_URL}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 inline-flex w-fit items-center gap-3 rounded-full border border-gold/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            Claim your Aureon ID
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col overflow-hidden border border-gold/20 bg-black/60 shadow-[0_0_60px_rgba(212,175,55,0.08)] backdrop-blur-xl"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-white/15" />
              <span className="h-2 w-2 rounded-full bg-white/15" />
              <span className="h-2 w-2 rounded-full bg-gold/50" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
              Aureon / Secure-Feed
            </span>
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#00FF41]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00FF41] animate-pulse-dot" />
              Live
            </span>
          </div>
          <div className="relative flex-1 overflow-hidden px-5 py-6" data-testid="feed-terminal">
            <div className="space-y-2.5">
              {lines.map((l, i) => (
                <p
                  key={`${i}-${l.text.slice(0, 10)}`}
                  className={`font-tabular text-[10px] leading-relaxed tracking-wider sm:text-xs ${
                    i === lines.length - 1
                      ? l.gold
                        ? "text-gold"
                        : "text-[#00FF41]"
                      : l.gold
                        ? "text-gold/40"
                        : "text-white/25"
                  }`}
                >
                  {l.text}
                </p>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_3px,rgba(255,255,255,0.015)_3px,rgba(255,255,255,0.015)_4px)]" />
          </div>
          <div className="flex items-center justify-between border-t border-white/10 px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-white/30">
            <span>Fig. 01 — The Feed</span>
            <span className="text-gold/60">AES-256 · TLS 1.3</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
